import { Bell, BellOff, Check, Hourglass, type LucideIcon } from "lucide-react";
import { cn } from "@/design-system/components/react";
import type { CaseState, NotifyCase } from "./types";

/** A case's state as a badge, plus "not_sent" (its alert failed). */
export type BadgeState = CaseState | "not_sent";

/** Every state's look in one place (AGENTS.md: state is never hue alone; the
 * icon and text label carry it). */
const BADGES: Record<BadgeState, { label: string; icon: LucideIcon; className: string }> = {
  // Dashed: not yet sent, lower emphasis.
  open: { label: "Open", icon: Hourglass, className: "border-dashed text-fg-muted" },
  // Solid border-strong + surface-2 lift: needs attention.
  alerted: { label: "Alerted", icon: Bell, className: "bg-surface-2 text-fg" },
  // Selected/affirmed, lower emphasis.
  acknowledged: {
    label: "Acknowledged",
    icon: Check,
    className: "border-transparent bg-signal-tint text-signal-text-tint",
  },
  // Still open (no push delivered): dashed like Open, told apart by icon + label.
  not_sent: { label: "Not sent", icon: BellOff, className: "border-dashed text-fg-muted" },
};

export function StateBadge({ state }: { state: BadgeState }) {
  const { label, icon: Icon, className } = BADGES[state];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-[1px] border border-border-strong px-2 py-px text-[11.5px] font-semibold",
        className,
      )}
    >
      <Icon aria-hidden className="size-3" strokeWidth={2.5} />
      {label}
    </span>
  );
}

/** The left rule a list row gets for its state (alerted rows need attention). */
export const rowRule = (state: CaseState) =>
  state === "alerted" ? "border-l-border-strong" : "border-l-transparent";

/** An open case whose alert reached no one (`alert_status: "failed"`) reads
 * "Not sent"; every other case reads its state. */
export const badgeState = (row: NotifyCase): BadgeState =>
  row.state === "open" && row.alert_status === "failed" ? "not_sent" : row.state;

/** Marks a test case (a `test-` study, not a real one). */
export function TestTag() {
  return (
    <span className="shrink-0 rounded-[1px] border border-border px-1 font-mono text-[10px] tracking-[0.1em] text-fg-dim">
      TEST
    </span>
  );
}
